'use client';

import { startTransition, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Inter } from 'next/font/google';
import { Button } from '@/lib/components/ui/button';
import * as Sentry from '@sentry/nextjs';
import { isStaleServerActionError } from '@/lib/utils/handle-error';
import { cn } from '@/lib/utils/helpers/cn';

const inter = Inter({
  subsets: ['latin'],
  style: ['normal'],
  display: 'swap',
});

export default function GlobalError({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();
  const isStaleAction = isStaleServerActionError(error);

  function handleReset() {
    startTransition(() => {
      reset();
      router.refresh();
    });
  }

  function handleReload() {
    window.location.reload();
  }

  useEffect(() => {
    // Report error to Sentry
    console.error(error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <body className={inter.className}>
        <div
          className={cn(
            'flex min-h-screen flex-col items-center justify-center px-6',
            isStaleAction ? 'bg-blue-50 text-blue-700' : 'bg-red-300'
          )}
        >
          <h1 className="m-auto my-4 text-3xl">
            {isStaleAction
              ? 'The application has been updated. Please refresh the page to continue.'
              : 'Something Went Wrong'}
          </h1>
          <Button
            className="mt-4  p-2"
            onClick={isStaleAction ? handleReload : handleReset}
          >
            {isStaleAction ? 'Reload' : 'Try Again'}
          </Button>
        </div>
      </body>
    </html>
  );
}
